import React, { useEffect, useContext, useRef, Fragment } from 'react';
import FadeIn from 'react-fade-in';
import { Observer } from 'mobx-react-lite';
import MaskedInput from 'react-text-mask'
import DateBox from 'devextreme-react/date-box';
import { GlobalHotKeys } from 'react-hotkeys';
import moment from 'moment';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModalComponent';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import RichTextEditor from '../../_shared/RichTextEditor';

import GroupPaymentCreateStore from '../../../../stores/GroupPaymentCreateStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';
import CacheStore from '../../../../stores/CacheStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as MaskKeys from '../../../../constants/maskKeys';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';
import * as ih from '../../../../utilities/invoiceHelper';

import './NewGroupPayment.scss';

function NewGroupPayment(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const confirmModalRef = useRef(null);
    const focusTimer = useRef(null);
    const newGroupPayment = useContext(GroupPaymentCreateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const cache = useContext(CacheStore);

    useEffect(() => {
        focusTimer.current = setTimeout(() => {
            quickDrawerFocus(props.drawer);
        }, 100);

        newGroupPayment.data.paymentDate = moment().startOf('day');

        return () => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (newGroupPayment.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleAmountChange = (event, id) => {
        const amount = event.target.value ? fn.parseCurrency(event.target.value) : null;
        const index = newGroupPayment.data.payments.findIndex(p => p.id === id);

        if (index >= 0) {
            newGroupPayment.data.payments[index].value = amount;
            newGroupPayment.hasUnsavedChanges = true;
        }
    }

    const handlePaymentDateChange = element => {
        const value = element.value;

        newGroupPayment.data.paymentDate = value ? moment(value).startOf('day') : moment().startOf('day');
        newGroupPayment.hasUnsavedChanges = true;
    }

    const handlePaymentMethodChange = event => {
        const paymentMethod = event.target.value;

        newGroupPayment.data.paymentMethod = paymentMethod;
        newGroupPayment.hasUnsavedChanges = true;
    }

    const handleNoteChange = (content, delta, source, editor) => {
        const note = editor.getText().replace(/(\r\n|\n|\r)/g, ' ').trim();
        const noteHtml = (content === '<p><br></p>') ? null : content;

        newGroupPayment.data.note = note;
        newGroupPayment.data.noteHtml = noteHtml;
        newGroupPayment.hasUnsavedChanges = true;
    }

    const handleSubmit = event => {
        event.preventDefault();
        if (fn.validateForm(validateRef.current)) {
            if (newGroupPayment.amount > newGroupPayment.remainingBalance) {
                confirmModalRef.current.show({
                    icon: <i className={`${oh.getIcon('purchase', 'payment')} text-danger mr-2`}></i>,
                    message: <>You cannot overpay a group payment. </>,
                    description: 'Please adjust the amount and try again.',
                    option1ClassName: 'd-none',
                    cancelText: 'Cancel',
                    onCancel: handleOverpayCancel
                })
            }
            else {
                newGroupPayment.save(true)
                    .then((data) => {
                        if (isMounted.current) {
                            if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                                props.onSuccess(event, { updated: true, data: data });
                            }
                        }
                    })
            }
        }
    }

    const handleOverpayCancel = event => {
        confirmModalRef.current.close();
    }

    const renderPaymentMethods = () => {
        let options = cache.getReferenceDataOptions('ValidGroupPaymentMethodTypes');

        return <Observer>{() =>
            <select
                id='new-group-payment-paid-by'
                className='custom-select form-control'
                value={newGroupPayment.data.paymentMethod ? newGroupPayment.data.paymentMethod : ''}
                onChange={handlePaymentMethodChange}
            >
                <option value=''></option>
                {
                    options.map((option, di) => {
                        return <option key={`payment_method_type_${di}`} value={option.key}>{ih.getPaymentMethodDescription(option)}</option>
                    })
                }
            </select>
        }</Observer>
    }

    return <>
        <Observer>{() =>
            <>
                {
                    (props.drawer === quickDrawer.drawerOpened) ?
                        <GlobalHotKeys
                            keyMap={{
                                close: ['esc'],
                            }}
                            handlers={{
                                close: event => {
                                    handleCancel(event)
                                },
                            }}
                            allowChanges={true}
                        /> : null
                }
            </>
        }</Observer>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <Observer>{() =>
                <fieldset disabled={newGroupPayment.isSaving}>
                    <div className='new-group-payment-container quick-drawer'>
                        <QuickDrawerHeader
                            drawer={props.drawer}
                            icon={oh.getIcon('purchase', 'payment')}
                            action='Purchase'
                            category='Create Group Payment'
                            className='purchases'
                            onCancel={handleCancel}
                        />
                        <div className='quick-drawer-body'>
                            <Observer>{() =>
                                <>
                                    {
                                        newGroupPayment.isReady ?
                                            <>
                                                {
                                                    newGroupPayment.data.payments && newGroupPayment.data.payments.length > 0 ?
                                                        <>
                                                            <FadeIn>
                                                                <div className='body-content'>
                                                                    <section>
                                                                        <div className='row'>
                                                                            <div className='col-4'>
                                                                                <label><small>Invoices Total</small></label>
                                                                                <Observer>{() =>
                                                                                    <strong className='d-block fs-xl'>{fn.formatCurrency(newGroupPayment.total)}</strong>
                                                                                }</Observer>
                                                                            </div>
                                                                            <div className='col-4'>
                                                                                <label><small className={((newGroupPayment.amount - newGroupPayment.remainingBalance) !== 0 ? 'text-danger' : '')}>Total Remaining</small></label>
                                                                                <Observer>{() =>
                                                                                    <strong className={'d-block fs-xl' + (newGroupPayment.amount - newGroupPayment.remainingBalance !== 0 ? ' text-danger' : '')}>{fn.formatCurrency((newGroupPayment.remainingBalance - newGroupPayment.amount))}</strong>
                                                                                }</Observer>
                                                                            </div>
                                                                            <div className='col-4'>
                                                                                <label><small className={(newGroupPayment.amount > 0.0 ? 'text-info' : '')}>Amount</small></label>
                                                                                <Observer>{() =>
                                                                                    <strong className={'d-block fs-xl' + (newGroupPayment.amount > 0.0 ? ' text-info' : '')}>{fn.formatCurrency(newGroupPayment.amount)}</strong>
                                                                                }</Observer>
                                                                            </div>
                                                                        </div>
                                                                    </section>
                                                                    <section>
                                                                        <div className='row'>
                                                                            <div className='col-6 validate validate-required'>
                                                                                <label htmlFor='new-group-payment-paid-by' className='required'><small>Paid by</small></label>
                                                                                {renderPaymentMethods()}
                                                                            </div>
                                                                            <div className='col-6'>
                                                                                <div className='form-group mb-0 validate validate-required'>
                                                                                    <label className='required'><small>Paid on (YYYY-MM-DD)</small></label>
                                                                                    <Observer>{() =>
                                                                                        <DateBox
                                                                                            id='new-group-payment-invoice-date'
                                                                                            type='date'
                                                                                            displayFormat={'yyyy-MM-dd'}
                                                                                            useMaskBehavior={false}
                                                                                            showClearButton={false}
                                                                                            min={newGroupPayment.purchases.reduce((max, purchase) => {
                                                                                                const invoiceDate = moment(purchase.invoiceDate).startOf('day');
                                                                                                if (invoiceDate.isAfter(max)) {
                                                                                                    max = invoiceDate.clone();
                                                                                                }
                                                                                                return max;
                                                                                            }, moment().startOf('day')).toDate()}
                                                                                            max={moment().startOf('day').toDate()}
                                                                                            value={newGroupPayment.data.paymentDate ? moment(newGroupPayment.data.paymentDate).toDate() : null}
                                                                                            onValueChanged={handlePaymentDateChange}
                                                                                            onOpened={() => { document.querySelector('.dx-datebox-wrapper').style.zIndex = fn.getHighestZIndex() + 1; }}
                                                                                        >
                                                                                        </DateBox>
                                                                                    }</Observer>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </section>
                                                                    <section>
                                                                        <div className='row'>
                                                                            <div className='col-12 validate validate-required'>
                                                                                <label htmlFor='new-group-payment-invoices' className='required'><small>Invoice(s)</small></label>
                                                                            </div>
                                                                        </div>
                                                                        {
                                                                            newGroupPayment.data.payments.map((paymentInfo, pi) => {
                                                                                const purchase = newGroupPayment.purchases.find(p => p.id === paymentInfo.id);

                                                                                return <Fragment
                                                                                    key={`new_group_payment_${pi}`}
                                                                                >
                                                                                    {
                                                                                        purchase ?
                                                                                            <div className='row align-items-center mb-4'>
                                                                                                <div className='col-8'>
                                                                                                    <p className='text-gray-600 mb-0'>Invoice # <strong>{purchase.invoiceNumber}</strong></p>
                                                                                                    <p className='text-gray-800 mb-1'><strong>{purchase.customerName}</strong></p>
                                                                                                    <table className='table table-borderless w-100 mb-0'>
                                                                                                        <colgroup>
                                                                                                            <col style={{ width: '25px' }}></col>
                                                                                                            <col></col>
                                                                                                        </colgroup>
                                                                                                        <tbody>
                                                                                                            {
                                                                                                                purchase.items.map((item, ti) => {
                                                                                                                    return <tr key={`new_group_payment_${pi}_${ti}`}>
                                                                                                                        <td className='p-0 pr-o'>{item.quantity}x</td>
                                                                                                                        <td className='p-0'>{item.descriptor}</td>
                                                                                                                    </tr>
                                                                                                                })
                                                                                                            }
                                                                                                        </tbody>
                                                                                                    </table>
                                                                                                </div>
                                                                                                <div className='col-4 validate validate-required'>
                                                                                                    <Observer>{() =>
                                                                                                        <MaskedInput
                                                                                                            id={`new-group-payment-amount-${pi}`}
                                                                                                            type='text'
                                                                                                            className='form-control currency-input text-right'
                                                                                                            mask={MaskKeys.CURRENCY_POSITIVE_MASK}
                                                                                                            autoComplete='off'
                                                                                                            value={newGroupPayment.data.payments[pi].value ? newGroupPayment.data.payments[pi].value : ''}
                                                                                                            onChange={(e) => { handleAmountChange(e, newGroupPayment.data.payments[pi].id) }}
                                                                                                        />
                                                                                                    }</Observer>
                                                                                                </div>
                                                                                            </div> : <></>
                                                                                    }
                                                                                </Fragment>
                                                                            })
                                                                        }
                                                                    </section>
                                                                    <section>
                                                                        <div className='row'>
                                                                            <div className='col-12'>
                                                                                <div className='form-group'>
                                                                                    <label><small>Note</small></label>
                                                                                    <div className='input-group'>
                                                                                        <Observer>{() =>
                                                                                            <RichTextEditor
                                                                                                mode='none'
                                                                                                disableTab={true}
                                                                                                value={newGroupPayment.data.noteHtml}
                                                                                                onChange={handleNoteChange}
                                                                                            />
                                                                                        }</Observer>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </section>
                                                                </div>
                                                            </FadeIn>
                                                        </> :
                                                        <>
                                                            <FadeIn>
                                                                <div className='body-content'>
                                                                    <section>
                                                                        <div className='row'>
                                                                            <div className='col-12'>
                                                                                <p>There are no outstanding invoices.</p>
                                                                            </div>
                                                                        </div>
                                                                    </section>
                                                                </div>
                                                            </FadeIn>
                                                        </>
                                                }
                                            </> : renderQuickDrawerLoading()
                                    }
                                </>
                            }</Observer>
                        </div>
                        <div className='quick-drawer-action pl-3'>
                            <div className='row'>
                                <div className='col-12'>
                                    <div className='float-right'>
                                        <button
                                            type='button'
                                            className='btn btn-link btn-cancel'
                                            onClick={handleCancel}
                                        >Cancel</button>
                                        {
                                            newGroupPayment.data.payments && newGroupPayment.data.payments.length > 0 ?
                                                <button
                                                    type='submit'
                                                    className='btn btn-success ml-2'
                                                >Save</button> : null
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </fieldset>
            }</Observer>
        </form>
        <BodyEnd>
            <Observer>{() =>
                <ConfirmModal ref={confirmModalRef} />
            }</Observer>
        </BodyEnd>
    </>
}

export default NewGroupPayment;